import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/shared/constants/routes'
import { FEATURE_FLAGS } from '@/shared/constants/feature-flags'
import type { FeatureFlag } from '@/shared/constants/feature-flags'
import {
  useFeatureEnabled,
  useFeatureFilter,
} from '@/shared/hooks/use-feature-enabled'
import type {
  FilterOption,
  GoalCardLayout,
  GoalCardLink,
  GoalCardSize,
  IdCardLink,
  PerkCardAccent,
  PerkCardAction,
} from '@/components/molecules'
import {
  downloadDocuments,
  exclusiveBenefits,
  getPaidBack,
  invoice,
  money,
  perkDirectVet,
  perkPharmacy,
  perkTelevet,
  perkWellfold,
  petDefaultIdCardImage,
  rentersDefaultIdCardImage,
  trackYourProcess,
} from '@/shared/assets'

export type HomePolicyFilter = 'all' | 'pet' | 'renters'

export interface HomePolicy {
  id: string
  type: Exclude<HomePolicyFilter, 'all'>
  name: string
  policyNumber: string
  photoUrl: string
}

export interface HomeGoal {
  id: string
  icon: string
  i18nKey: string
  values?: Record<string, string | number>
  layout?: GoalCardLayout
  size: GoalCardSize
  link?: GoalCardLink
  flag?: FeatureFlag
}

export interface HomePerk {
  id: string
  image: string
  title: string
  i18nKey: string
  accent: PerkCardAccent
  action: PerkCardAction
}

// TODO: replace with the policies API once it's migrated.
const DUMMY_POLICIES: HomePolicy[] = [
  {
    id: 'dummy-policy-1',
    type: 'pet',
    name: 'Amapola',
    policyNumber: '#12345678',
    photoUrl: petDefaultIdCardImage,
  },
  {
    id: 'dummy-policy-2',
    type: 'renters',
    name: 'Home',
    policyNumber: '#12345678',
    photoUrl: rentersDefaultIdCardImage,
  },
  {
    id: 'dummy-policy-3',
    type: 'pet',
    name: 'Rocky',
    policyNumber: '#12345678',
    photoUrl: petDefaultIdCardImage,
  },
]

// TODO: replace with the goals API (waiting period, pending steps, etc.).
const DUMMY_WAITING_DAYS = 10
const DUMMY_PET_NAME = 'Amapola'

export function useHomeMobile() {
  const { t } = useTranslation()

  const [policyFilter, setPolicyFilter] = useState<HomePolicyFilter>('all')
  const policyFilters: FilterOption<HomePolicyFilter>[] = [
    { key: 'all', label: t('home.policies.filters.all') },
    { key: 'pet', label: t('home.policies.filters.pets') },
    { key: 'renters', label: t('home.policies.filters.renters') },
  ]
  const policies = DUMMY_POLICIES.filter(
    (policy) => policyFilter === 'all' || policy.type === policyFilter,
  )
  // TODO: link to the policy coverage detail once it exists.
  const policyLink: IdCardLink = {
    label: t('home.policies.whatsCovered'),
    onClick: () => undefined,
  }

  const showGoals = !!useFeatureEnabled(FEATURE_FLAGS.WEB_APP_GOALS)
  const goals = useFeatureFilter<HomeGoal>([
    {
      id: 'waiting-period',
      icon: exclusiveBenefits,
      i18nKey: 'home.goals.waitingPeriod',
      values: { count: DUMMY_WAITING_DAYS },
      layout: 'icon-right',
      size: 'sm',
    },
    {
      id: 'whats-covered',
      icon: invoice,
      i18nKey: 'home.goals.whatsCovered',
      size: 'md',
      link: { label: t('home.goals.seeDetails'), to: ROUTES.PERKS },
      flag: FEATURE_FLAGS.WEB_APP_PERKS,
    },
    {
      id: 'confirm-payment',
      icon: money,
      i18nKey: 'home.goals.confirmPayment',
      size: 'lg',
      link: { label: t('home.goals.confirmNow'), to: ROUTES.PAYMENT },
      flag: FEATURE_FLAGS.WEB_APP_PAYMENT,
    },
    {
      id: 'download-documents',
      icon: downloadDocuments,
      i18nKey: 'home.goals.downloadDocuments',
      size: 'sm',
      flag: FEATURE_FLAGS.WEB_APP_DOCUMENTS,
    },
    {
      id: 'finish-setup',
      icon: trackYourProcess,
      i18nKey: 'home.goals.finishSetup',
      values: { name: DUMMY_PET_NAME },
      size: 'md',
      link: { label: t('home.goals.goToFinish'), to: ROUTES.ONBOARDING_PET },
    },
    {
      id: 'add-reimbursement',
      icon: getPaidBack,
      i18nKey: 'home.goals.addReimbursement',
      size: 'lg',
      link: {
        label: t('home.goals.addAccount'),
        to: ROUTES.PAYMENT_OUT_UPDATE,
      },
      flag: FEATURE_FLAGS.WEB_APP_PAYMENT,
    },
  ])
  // Masonry: alternate goals between the two columns to keep them balanced.
  const goalColumns = [
    goals.filter((_, index) => index % 2 === 0),
    goals.filter((_, index) => index % 2 === 1),
  ]

  const showPerks = !!useFeatureEnabled(FEATURE_FLAGS.WEB_APP_PERKS)
  const perks: HomePerk[] = [
    {
      id: 'televet',
      image: perkTelevet,
      title: t('home.perks.televet.title'),
      i18nKey: 'home.perks.televet.description',
      accent: 'indigo',
      action: { label: t('home.perks.televet.cta'), onClick: () => {} },
    },
    {
      id: 'pharmacy',
      image: perkPharmacy,
      title: t('home.perks.pharmacy.title'),
      i18nKey: 'home.perks.pharmacy.description',
      accent: 'indigo',
      action: { label: t('home.perks.pharmacy.cta'), onClick: () => {} },
    },
    {
      id: 'direct-vet',
      image: perkDirectVet,
      title: t('home.perks.directVet.title'),
      i18nKey: 'home.perks.directVet.description',
      accent: 'indigo',
      action: { label: t('home.perks.directVet.cta'), onClick: () => {} },
    },
    {
      id: 'wellfold',
      image: perkWellfold,
      title: t('home.perks.wellfold.title'),
      i18nKey: 'home.perks.wellfold.description',
      accent: 'indigo',
      action: { label: t('home.perks.wellfold.cta'), onClick: () => {} },
    },
  ]

  return {
    policyFilters,
    policyFilter,
    setPolicyFilter,
    policies,
    policyLink,
    showGoals: showGoals && goals.length > 0,
    goalColumns,
    showPerks,
    perks,
  }
}
